import { FC, memo, useMemo, useRef, useEffect, useState } from 'react';

import { EllipsisOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, Tooltip } from 'antd';

import ReportChart from '../../../../shared/common/components/reports/chart/ReportChart';
import { ReportTable } from '../../../../shared/common/components/reports/report-table/ReportTable';
import ClarityCursorHandGrabLine from '../../../../shared/common/components/ui/icons/ClarityCursorHandGrabLine';
import { useTypedSelector } from '../../../../shared/common/hooks/useTypedSelector';
import { DashboardBlockProvider } from '../../context/DashboardBlockContext';
import { DashboardComponent, GRID_CONSTANTS } from '../../types';
import {
  getReportTypeFromDate,
  chartToTableReportType,
  calculateChartHeight,
} from '../../utils/layoutMigration';
import Card from '../Card';
import Heading from '../Heading';

import styles from './DashboardBlock.module.scss';

/** Типы графиков, данные которых формируются из tableData (не из отдельного chart API) */
const TABLE_DEPENDENT_CHART_TYPES = ['pie', 'bar', 'treemap', 'tunnel', 'speedometer'];

interface DashboardBlockProps {
  /** Данные компонента */
  component: DashboardComponent;
  /** Блок выделен (Cmd+A) */
  isSelected?: boolean;
  /** Обработчик редактирования */
  onEdit: (id: string) => void;
  /** Обработчик клонирования */
  onClone: (id: string) => void;
  /** Обработчик удаления */
  onDelete: (id: string) => void;
  /** Обработчик открытия полного отчёта */
  onOpenFullReport: (component: DashboardComponent) => void;
}

/**
 * Компонент блока дашборда.
 * Отображает:
 * - Заголовок с меню действий
 * - Контент в зависимости от типа (chart, table, number, heading)
 * - Иконку перетаскивания
 *
 * Обёрнут в DashboardBlockProvider для предоставления контекста
 * вложенным компонентам (ReportChart, ReportTable).
 */
const DashboardBlock: FC<DashboardBlockProps> = ({
  component,
  isSelected = false,
  onEdit,
  onClone,
  onDelete,
  onOpenFullReport,
}) => {
  const { componentType, title, data, layoutData } = component;

  const isChartBlock = componentType === 'chart';
  const isTableBlock = componentType === 'table';
  const isNumberBlock = componentType === 'number';
  const isHeadingBlock = componentType === 'heading';

  // Ref на .container для получения реальной ширины блока (передаётся в ReportTable для горизонтального скролла)
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [blockWidth, setBlockWidth] = useState(0);

  useEffect(() => {
    if (!isTableBlock) return;
    const el = tableContainerRef.current;
    if (!el) return;
    const getContentWidth = () => {
      const style = window.getComputedStyle(el);
      return Math.max(0, el.clientWidth - parseFloat(style.paddingLeft || '0') - parseFloat(style.paddingRight || '0'));
    };
    setBlockWidth(getContentWidth());
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => setBlockWidth(getContentWidth()));
      ro.observe(el);
      return () => ro.disconnect();
    }
  }, [isTableBlock]);

  const blockMeta = useTypedSelector((state) =>
    data.blockId ? state.meta.blocks[data.blockId] : null
  );
  // Для chart-блоков: pie/bar/treemap/tunnel/speedometer берут данные из tableData → isTableLoading,
  // остальные (line/area/bubble) — из chart API → isChartLoading.
  // isTableLoading инициализируется как true и не сбрасывается для chart-блоков без ReportTable.
  const isBlockLoading = isChartBlock
    ? TABLE_DEPENDENT_CHART_TYPES.includes(data?.chartType || '')
      ? blockMeta?.isTableLoading
      : blockMeta?.isChartLoading
    : isTableBlock
      ? blockMeta?.isTableLoading
      : false;

  /**
   * Меню действий блока.
   */
  const menu = useMemo(
    () => (
      <Menu>
        <Menu.Item key="edit" onClick={() => onEdit(layoutData.i)}>
          <Button type="text">Редактировать</Button>
        </Menu.Item>
        <Menu.Item key="clone" onClick={() => onClone(layoutData.i)}>
          <Button type="text">Копировать</Button>
        </Menu.Item>
        <Menu.Item key="delete" onClick={() => onDelete(layoutData.i)}>
          <Button type="text">Удалить</Button>
        </Menu.Item>
      </Menu>
    ),
    [component, layoutData.i, isChartBlock, isTableBlock, onEdit, onClone, onDelete, onOpenFullReport]
  );

  /**
   * Стили контента в зависимости от типа блока.
   */
  const contentStyles = useMemo(() => {
    switch (componentType) {
      case 'table':
        return { width: '100%', height: '100%' };
      case 'number':
        return { marginTop: '0', height: '100%' };
      case 'chart':
        return {
          height: '100%',
          ...(data?.chartType === 'pie' ? { paddingTop: '4px', marginLeft: '-12px', marginRight: '-12px' } : {}),
        };
      case 'heading':
        return { marginTop: '-24px', height: '100%' };
      default:
        return { marginTop: '-24px', width: '100px', height: '100px' };
    }
  }, [componentType, data?.chartType]);

  /**
   * Рендер контента блока.
   */
  const renderContent = useMemo(() => {
    if (isChartBlock) {
      const chartReportType = getReportTypeFromDate(data.date);
      const tableReportType = chartToTableReportType(chartReportType);
      const chartHeight = calculateChartHeight(layoutData.h, 45);
      const needsTableData = TABLE_DEPENDENT_CHART_TYPES.includes(data?.chartType || '');

      return (
        <>
          {/* Скрытый ReportTable для загрузки tableData (нужен pie/bar/treemap/tunnel/speedometer) */}
          {needsTableData && (
            <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}>
              <ReportTable
                reportType={tableReportType}
                blockId={data.blockId}
                displayMode="compact"
                isModal={false}
                date={data.date}
                hideGroupingsPanel={true}
                hideMetricsPanel={true}
              />
            </div>
          )}
          <ReportChart
            reportType={chartReportType}
            blockId={data.blockId}
            displayMode="compact"
            isModal={false}
            date={data.date}
            hideChartTypeButtons={true}
            hideTitle={true}
            chartHeight={chartHeight}
          />
        </>
      );
    }

    if (isTableBlock) {
      const tableReportType = chartToTableReportType(getReportTypeFromDate(data.date));

      return (
        <ReportTable
          reportType={tableReportType}
          blockId={data.blockId}
          displayMode="compact"
          isModal={false}
          date={data.date}
          hideGroupingsPanel={true}
          hideMetricsPanel={true}
          hideSelectionControls={true}
          hideTotalCheckbox={true}
          hideMetricButtons={true}
          hideTotalChosenRow={true}
          hideRowCheckboxes={true}
          blockContainerWidth={blockWidth || undefined}
        />
      );
    }

    if (isNumberBlock) {
      return <Card {...data} isShow={true} />;
    }

    if (isHeadingBlock) {
      return <Heading {...data} isShow={true} />;
    }

    return null;
  }, [isChartBlock, isTableBlock, isNumberBlock, isHeadingBlock, data, layoutData.h]);

  // Пропускаем блоки без контента
  if (!isChartBlock && !isTableBlock && !isNumberBlock && !isHeadingBlock) {
    return null;
  }

  /**
   * CSS-классы контейнера.
   */
  const containerClassName = [
    styles.componentContainer,
    styles.smoothComponent,
    isSelected ? styles.selected : '',
    isChartBlock ? styles.chartBlock : '',
    isTableBlock ? styles.tableBlock : '',
    isNumberBlock ? styles.numberBlock : '',
    isHeadingBlock ? styles.heading : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <DashboardBlockProvider
      blockId={data.blockId}
      mode="dashboard"
      date={data.date}
      componentType={componentType}
    >
      <div
        className={containerClassName}
        style={{
          overflow: 'hidden',
          boxShadow: 'none',
          ...(isHeadingBlock ? { background: 'transparent' } : {}),
        }}
        id={`component-${data.blockId}`}
      >
        {/* Заголовок блока */}
        <div className={styles.headerBlock}>
          <div className={`${styles.dragIcon} drag-handler`} data-testid="drag-handler">
            <ClarityCursorHandGrabLine className="drag-icon" />
          </div>
          <div className={styles.dropdownContainer}>
            <Dropdown overlay={menu} trigger={['click']}>
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          </div>
          <div className={styles.titleContainer}>
            <h2
              className={styles.title}
              style={{
                ...(isHeadingBlock && data?.headingStyles
                  ? {
                      fontSize: `${data.headingStyles.fontSize}px`,
                      fontWeight: data.headingStyles.fontWeight,
                      color: data.headingStyles.color,
                    }
                  : isHeadingBlock
                  ? { fontWeight: 800 }
                  : {}),
                marginBottom: 0,
              }}
            >
              {title}
            </h2>
            {data?.description && (
              <Tooltip
                placement="bottom"
                title={
                  <div
                    className="page-title-tooltip-text"
                    dangerouslySetInnerHTML={{ __html: data.description }}
                  />
                }
                color="#fff"
                zIndex={9999}
                trigger="click"
                overlayClassName="page-title-tooltip"
              >
                <QuestionCircleOutlined className={styles.descriptionIcon} />
              </Tooltip>
            )}
          </div>
        </div>

        {/* Контент блока */}
        <div
          ref={isTableBlock ? tableContainerRef : undefined}
          className={styles.container}
          style={isTableBlock ? { paddingBottom: 0 } : undefined}
        >
          {isBlockLoading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner} />
            </div>
          )}
          <div style={{ width: '100%', height: '100%', ...contentStyles }}>
            {renderContent}
          </div>
        </div>
      </div>
    </DashboardBlockProvider>
  );
};

export default memo(DashboardBlock);
